import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';

const compact = (v) => {
  const n = Number(v || 0);
  if (n === 0) return '$0';
  if (n >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `$${n.toLocaleString()}`;
};

const px = 0.75;
const py = 0.4;
const cellSx = { px, py, fontSize: 11, whiteSpace: 'nowrap' };
const hdrSx = { ...cellSx, fontWeight: 700 };
const subHdrSx = { ...cellSx, fontWeight: 600 };

const br1 = { borderRight: '1px solid #ddd' };
const brHic = { borderRight: '2px solid #0d3c61' };
const brSvc = { borderRight: '2px solid #7b1fa2' };

const typeChip = (type, origin) => {
  const isPaid = type === 'paid';
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <Chip
        label={isPaid ? 'Paid' : 'Organic'}
        size="small"
        sx={{
          height: 18, fontSize: 10, fontWeight: 700,
          bgcolor: isPaid ? '#e3f2fd' : '#e8f5e9',
          color: isPaid ? '#1565c0' : '#2e7d32',
        }}
      />
      {origin === 'callrail' && (
        <Chip label="CR" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: '#fff3e0', color: '#e65100' }} />
      )}
    </Box>
  );
};

const SourceTable = ({ sources, totals, attribution }) => {
  const o = totals.overall;
  const sorted = [...sources].sort((a, b) => b.totalRevenue - a.totalRevenue);

  return (
    <Paper sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <CampaignIcon sx={{ color: '#0d3c61', fontSize: 22 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#0d3c61', fontSize: 16 }}>
          Marketing Attribution by Source
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {attribution.callRailAttributed} CallRail / {attribution.crmFallback} CRM fallback
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { px, py } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...hdrSx, ...br1, bgcolor: '#e8eaf6', minWidth: 28 }} rowSpan={2}>#</TableCell>
              <TableCell sx={{ ...hdrSx, ...br1, bgcolor: '#e8eaf6', minWidth: 130 }} rowSpan={2}>Source</TableCell>
              <TableCell sx={{ ...hdrSx, ...br1, bgcolor: '#e8eaf6', minWidth: 80 }} rowSpan={2}>Type</TableCell>
              <TableCell colSpan={4} align="center" sx={{ ...hdrSx, ...brHic, bgcolor: '#e3f2fd', color: '#1565c0', py: 0.3, borderBottom: '1px solid #ccc' }}>Calls</TableCell>
              <TableCell sx={{ ...hdrSx, ...brHic, bgcolor: '#e8eaf6' }} rowSpan={2} align="center">Appts</TableCell>
              <TableCell colSpan={3} align="center" sx={{ ...hdrSx, ...brSvc, bgcolor: '#e3f2fd', color: '#0d3c61', py: 0.3, borderBottom: '1px solid #ccc' }}>HIC (Installation)</TableCell>
              <TableCell colSpan={2} align="center" sx={{ ...hdrSx, ...br1, bgcolor: '#f3e5f5', color: '#7b1fa2', py: 0.3, borderBottom: '1px solid #ccc' }}>SVC (Service)</TableCell>
              <TableCell sx={{ ...hdrSx, bgcolor: '#e0f2f1', color: '#00695c' }} rowSpan={2} align="right">Total Rev</TableCell>
            </TableRow>
            <TableRow>
              {[
                { l: 'Total', c: '#1565c0' },
                { l: '1st', c: '#1565c0' },
                { l: 'Ans', c: '#43a047' },
                { l: 'Miss', c: '#e53935', sx: brHic },
                { l: 'Prop', c: '#0d3c61' },
                { l: 'Sold', c: '#4caf50' },
                { l: 'Rev', c: '#0d3c61', a: 'right', sx: brSvc },
                { l: 'Prop', c: '#7b1fa2' },
                { l: 'Rev', c: '#7b1fa2', a: 'right', sx: br1 },
              ].map(({ l, c, a, sx: extra }, idx) => (
                <TableCell key={idx} align={a || 'center'} sx={{ ...subHdrSx, color: c, bgcolor: '#f5f5f5', ...(extra || {}) }}>{l}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row, i) => {
              const bg = i % 2 === 0 ? '#fff' : '#fafafa';
              return (
                <TableRow key={row.sourceName} hover sx={{ bgcolor: bg }}>
                  <TableCell sx={{ ...cellSx, ...br1 }}>{i + 1}</TableCell>
                  <TableCell sx={{ ...cellSx, ...br1, fontWeight: 600 }}>{row.sourceName}</TableCell>
                  <TableCell sx={{ ...cellSx, ...br1 }}>{typeChip(row.sourceType, row.origin)}</TableCell>
                  <TableCell align="center" sx={{ ...cellSx, color: '#1565c0' }}>{row.calls || 0}</TableCell>
                  <TableCell align="center" sx={{ ...cellSx }}>{row.firstTimeCalls || 0}</TableCell>
                  <TableCell align="center" sx={{ ...cellSx, color: '#43a047' }}>{row.answeredCalls || 0}</TableCell>
                  <TableCell align="center" sx={{ ...cellSx, color: '#e53935', ...brHic }}>{row.missedCalls || 0}</TableCell>
                  <TableCell align="center" sx={{ ...cellSx, ...brHic, color: '#ff9800', fontWeight: 600 }}>{row.appointments || 0}</TableCell>
                  <TableCell align="center" sx={{ ...cellSx, color: '#0d3c61' }}>{row.hicProposals || 0}</TableCell>
                  <TableCell align="center" sx={{ ...cellSx, color: '#4caf50', fontWeight: 600 }}>{row.hicSold || 0}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, color: '#0d3c61', ...brSvc }}>{compact(row.hicRevenue)}</TableCell>
                  <TableCell align="center" sx={{ ...cellSx, color: '#7b1fa2' }}>{row.svcProposals || 0}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, color: '#7b1fa2', ...br1 }}>{compact(row.svcRevenue)}</TableCell>
                  <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: '#00695c' }}>{compact(row.totalRevenue)}</TableCell>
                </TableRow>
              );
            })}
            {sorted.length > 0 && (
              <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                <TableCell sx={{ ...hdrSx, ...br1 }} />
                <TableCell sx={{ ...hdrSx, ...br1 }}>TOTAL</TableCell>
                <TableCell sx={{ ...hdrSx, ...br1 }} />
                <TableCell align="center" sx={{ ...hdrSx, color: '#1565c0' }}>{o.calls}</TableCell>
                <TableCell align="center" sx={{ ...hdrSx }}>{o.firstTimeCalls}</TableCell>
                <TableCell align="center" sx={{ ...hdrSx, color: '#43a047' }}>{o.answeredCalls}</TableCell>
                <TableCell align="center" sx={{ ...hdrSx, color: '#e53935', ...brHic }}>{o.missedCalls}</TableCell>
                <TableCell align="center" sx={{ ...hdrSx, ...brHic, color: '#ff9800' }}>{o.appointments}</TableCell>
                <TableCell align="center" sx={{ ...hdrSx, color: '#0d3c61' }}>{o.hicProposals}</TableCell>
                <TableCell align="center" sx={{ ...hdrSx, color: '#4caf50' }}>{o.hicSold}</TableCell>
                <TableCell align="right" sx={{ ...hdrSx, color: '#0d3c61', ...brSvc }}>{compact(o.hicRevenue)}</TableCell>
                <TableCell align="center" sx={{ ...hdrSx, color: '#7b1fa2' }}>{o.svcProposals}</TableCell>
                <TableCell align="right" sx={{ ...hdrSx, color: '#7b1fa2', ...br1 }}>{compact(o.svcRevenue)}</TableCell>
                <TableCell align="right" sx={{ ...hdrSx, color: '#00695c' }}>{compact(o.totalRevenue)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default SourceTable;
